import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  Paper,
  Button,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PlayCircle as PlayCircleIcon,
  CalendarToday as CalendarIcon,
  Timer as TimerIcon,
  Book as BookIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  progressService,
  UserProgress,
  DevotionalProgress,
} from '../services/progressService';
import { Devotional } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface DevotionalWithProgress extends Devotional {
  userProgress: DevotionalProgress | null;
  isUnlocked: boolean;
}

const ViewTrail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useAuth();
  const [trailInfo, setTrailInfo] = useState<{ id: string; title: string; description: string; thumbnailUrl: string } | null>(null);
  const [devotionals, setDevotionals] = useState<DevotionalWithProgress[]>([]);
  const [selectedDevotional, setSelectedDevotional] = useState<DevotionalWithProgress | null>(null);
  const [progress, setProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const navigate = useNavigate();

  const fetchTrailWithProgress = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      if (!isAuthenticated) {
        // Usuário não autenticado - redirecionar para login
        navigate('/login');
        return;
      }

      const data = await progressService.getTrailWithProgress(id);
      setTrailInfo(data.trail);
      setProgress(data.progress);
      setDevotionals(data.devotionals);

      // Selecionar o devocional do dia atual
      const currentDevotional = data.devotionals.find(
        (d) => d.dayNumber === data.progress.currentDay && d.isUnlocked
      );
      setSelectedDevotional(currentDevotional || data.devotionals[0] || null);

      setError('');
    } catch (err: any) {
      setError('Erro ao carregar trilha');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, isAuthenticated, navigate]);

  useEffect(() => {
    fetchTrailWithProgress();
  }, [fetchTrailWithProgress]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatUnlockTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return `${diffHours}h ${diffMins}min`;
    }
    return `${diffMins} minutos`;
  };

  const handleDevotionalClick = (devotional: DevotionalWithProgress) => {
    if (!devotional.isUnlocked) {
      setSnackbar({
        open: true,
        message: 'Complete o devocional do dia atual primeiro!',
        severity: 'info',
      });
      return;
    }

    setSelectedDevotional(devotional);
  };

  const handleMarkComplete = async () => {
    if (!selectedDevotional || !id) return;

    // Verifica se já está concluído
    if (selectedDevotional.userProgress?.isCompleted) {
      setSnackbar({
        open: true,
        message: 'Devocional já foi concluído!',
        severity: 'info',
      });
      return;
    }

    // Verifica se é o dia atual
    if (selectedDevotional.dayNumber !== progress?.currentDay) {
      setSnackbar({
        open: true,
        message: 'Você só pode concluir o devocional do dia atual.',
        severity: 'error',
      });
      return;
    }

    try {
      setCompleting(true);
      await progressService.markComplete(id, selectedDevotional.id);

      setSnackbar({
        open: true,
        message: 'Devocional concluído! O próximo será liberado amanhã às 0h.',
        severity: 'success',
      });

      // Recarregar dados
      await fetchTrailWithProgress();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Erro ao marcar como concluído',
        severity: 'error',
      });
    } finally {
      setCompleting(false);
    }
  };

  const isCurrentDay = selectedDevotional?.dayNumber === progress?.currentDay;
  const isAlreadyCompleted = selectedDevotional?.userProgress?.isCompleted;
  const canComplete = isCurrentDay && !isAlreadyCompleted && selectedDevotional?.isUnlocked;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
          background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 50%, #FFF9C4 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#4CAF50' }} />
      </Box>
    );
  }

  if (!trailInfo) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error">Trilha não encontrada</Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 50%, #FFF9C4 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(circle at 20px 20px, rgba(76, 175, 80, 0.05) 2px, transparent 0)`,
          backgroundSize: '40px 40px',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 4, '& .MuiBreadcrumbs-separator': { color: '#689F38' } }}>
          <Link
            underline="hover"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate('/explore');
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: '#2E7D32',
              fontWeight: 500,
            }}
          >
            <ArrowBackIcon fontSize="small" />
            Trilhas
          </Link>
          <Typography sx={{ color: '#558B2F', fontWeight: 600 }}>
            {trailInfo.title}
          </Typography>
        </Breadcrumbs>

        {/* Progress Bar */}
        {progress && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
              border: '1px solid rgba(76, 175, 80, 0.2)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#1B5E20' }}>
                Seu progresso: Dia {progress.currentDay} de {devotionals.length}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 200,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: 'rgba(255,255,255,0.8)',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${(progress.currentDay / devotionals.length) * 100}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #4CAF50 0%, #66BB6A 100%)',
                      borderRadius: 4,
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 600 }}>
                  {Math.round((progress.currentDay / devotionals.length) * 100)}%
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* Player Section */}
          <Grid item xs={12} lg={8}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(76, 175, 80, 0.15)',
                border: '1px solid rgba(76, 175, 80, 0.1)',
              }}
            >
              {/* Video Player */}
              {selectedDevotional && (
                <Box
                  sx={{
                    position: 'relative',
                    paddingTop: '56.25%',
                    backgroundColor: '#000',
                  }}
                >
                  <iframe
                    src={selectedDevotional.videoUrl}
                    title={selectedDevotional.title}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </Box>
              )}

              {/* Devotional Info */}
              {selectedDevotional && (
                <CardContent sx={{ p: 4, background: 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(232,245,233,0.2))' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={`Dia ${selectedDevotional.dayNumber}`}
                        size="medium"
                        sx={{
                          background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                          color: '#1B5E20',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          border: '2px solid rgba(76, 175, 80, 0.3)',
                          px: 2,
                        }}
                      />
                      {isAlreadyCompleted && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Concluído"
                          size="medium"
                          sx={{
                            bgcolor: '#4CAF50',
                            color: 'white',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: 'white' },
                          }}
                        />
                      )}
                    </Box>

                    {/* Botão Concluir */}
                    {canComplete && (
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleMarkComplete}
                        disabled={completing}
                        startIcon={completing ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                        sx={{
                          background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
                          fontWeight: 700,
                          px: 4,
                          py: 1.5,
                          borderRadius: 3,
                          textTransform: 'none',
                          fontSize: '1rem',
                          boxShadow: '0 4px 14px rgba(76, 175, 80, 0.4)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #43A047 0%, #1B5E20 100%)',
                          },
                        }}
                      >
                        {completing ? 'Concluindo...' : 'Concluir Devocional'}
                      </Button>
                    )}
                  </Box>

                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      mb: 3,
                      fontSize: { xs: '1.8rem', md: '2.5rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    {selectedDevotional.title}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
                    {selectedDevotional.bibleReference && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                        <BookIcon sx={{ fontSize: 22, color: '#4CAF50' }} />
                        <Typography variant="body1" sx={{ color: '#2E7D32', fontWeight: 500 }}>
                          {selectedDevotional.bibleReference}
                        </Typography>
                      </Box>
                    )}
                    {selectedDevotional.duration && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                        <TimerIcon sx={{ fontSize: 22, color: '#4CAF50' }} />
                        <Typography variant="body1" sx={{ color: '#2E7D32', fontWeight: 500 }}>
                          {formatDuration(selectedDevotional.duration)}
                        </Typography>
                      </Box>
                    )}
                    {selectedDevotional.publishDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                        <CalendarIcon sx={{ fontSize: 22, color: '#4CAF50' }} />
                        <Typography variant="body1" sx={{ color: '#2E7D32', fontWeight: 500 }}>
                          {formatDate(selectedDevotional.publishDate as unknown as string)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 3, borderColor: 'rgba(76, 175, 80, 0.2)' }} />

                  {selectedDevotional.verseText && (
                    <Box
                      sx={{
                        p: 4,
                        background: 'linear-gradient(135deg, rgba(232, 245, 233, 0.5) 0%, rgba(241, 248, 233, 0.3) 100%)',
                        borderLeft: '6px solid #4CAF50',
                        borderRadius: 2,
                        mb: 4,
                        position: 'relative',
                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.1)',
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontStyle: 'italic',
                          color: '#1B5E20',
                          lineHeight: 1.8,
                          fontSize: '1.15rem',
                        }}
                      >
                        "{selectedDevotional.verseText}"
                      </Typography>
                      {selectedDevotional.bibleReference && (
                        <Typography
                          variant="body1"
                          sx={{
                            mt: 2,
                            color: '#2E7D32',
                            fontWeight: 600,
                            textAlign: 'right',
                            fontSize: '1rem',
                          }}
                        >
                          — {selectedDevotional.bibleReference}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {selectedDevotional.description && (
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: '#1B5E20',
                          mb: 3,
                        }}
                      >
                        Reflexão
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#558B2F',
                          lineHeight: 1.9,
                          whiteSpace: 'pre-line',
                          fontSize: '1.05rem',
                        }}
                      >
                        {selectedDevotional.description}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              )}
            </Paper>
          </Grid>

          {/* Playlist Sidebar */}
          <Grid item xs={12} lg={4}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(76, 175, 80, 0.15)',
                border: '1px solid rgba(76, 175, 80, 0.1)',
                maxHeight: { lg: 'calc(100vh - 180px)' },
                position: { lg: 'sticky' },
                top: { lg: 100 },
              }}
            >
              <Box
                sx={{
                  p: 3,
                  background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 50%, #4CAF50 100%)',
                  color: 'white',
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {trailInfo.title}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.95, mt: 1, fontWeight: 500 }}>
                  {devotionals.length} devocionais
                </Typography>
              </Box>

              <Box sx={{ maxHeight: { lg: 'calc(100vh - 320px)' }, overflowY: 'auto' }}>
                {devotionals.map((devotional, index) => {
                  const isSelected = selectedDevotional?.id === devotional.id;
                  const isCompleted = devotional.userProgress?.isCompleted;
                  const isLocked = !devotional.isUnlocked;
                  const isCurrent = devotional.dayNumber === progress?.currentDay;

                  return (
                    <Card
                      key={devotional.id}
                      sx={{
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        opacity: isLocked ? 0.6 : 1,
                        backgroundColor: isSelected ? '#E6F4EA' : isCurrent ? '#FFF8E1' : 'transparent',
                        borderRadius: 0,
                        borderBottom: '1px solid #E2E8F0',
                        borderLeft: isCurrent ? '4px solid #4CAF50' : 'none',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: isSelected ? '#E6F4EA' : isLocked ? 'transparent' : '#F7FAFC',
                        },
                      }}
                      onClick={() => handleDevotionalClick(devotional)}
                      elevation={0}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {/* Thumbnail */}
                          <Box
                            sx={{
                              width: 100,
                              height: 56,
                              borderRadius: 1,
                              overflow: 'hidden',
                              flexShrink: 0,
                              position: 'relative',
                              backgroundColor: '#E2E8F0',
                              backgroundImage: devotional.thumbnailUrl
                                ? `url(${devotional.thumbnailUrl})`
                                : 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: isLocked ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.3)',
                              }}
                            >
                              {isLocked ? (
                                <LockIcon sx={{ fontSize: 24, color: 'white' }} />
                              ) : isCompleted ? (
                                <CheckCircleIcon sx={{ fontSize: 24, color: '#4CAF50' }} />
                              ) : (
                                <PlayCircleIcon sx={{ fontSize: 24, color: 'white' }} />
                              )}
                            </Box>
                          </Box>

                          {/* Info */}
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  color: isCurrent ? '#F57C00' : '#4CAF50',
                                }}
                              >
                                DIA {devotional.dayNumber}
                              </Typography>
                              {isCompleted && (
                                <Chip
                                  label="Feito"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    bgcolor: '#4CAF50',
                                    color: 'white',
                                  }}
                                />
                              )}
                              {isCurrent && !isCompleted && (
                                <Chip
                                  label="Hoje"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    bgcolor: '#FF9800',
                                    color: 'white',
                                  }}
                                />
                              )}
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: '#2D3748',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {devotional.title}
                            </Typography>
                            {devotional.bibleReference && (
                              <Typography variant="caption" sx={{ color: '#718096' }}>
                                {devotional.bibleReference}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>

              {/* Próximo desbloqueio */}
              {progress?.nextUnlockAt && new Date(progress.nextUnlockAt) > new Date() && (
                <Box
                  sx={{
                    p: 2,
                    background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)',
                    borderTop: '1px solid rgba(255, 152, 0, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <ScheduleIcon sx={{ color: '#F57C00' }} />
                  <Typography variant="body2" sx={{ color: '#E65100', fontWeight: 600 }}>
                    Próximo em {formatUnlockTime(progress.nextUnlockAt)}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewTrail;
